import React, { useRef } from "react";
import { HiOutlineCalendarDays, HiOutlineClock } from "react-icons/hi2";

const fieldWrap =
  "flex items-center gap-2 w-full min-h-[48px] sm:min-h-[42px] px-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 focus-within:ring-2 focus-within:ring-[#5932EA]/30 focus-within:border-[#5932EA] transition-colors";

const inputClass =
  "flex-1 min-w-0 bg-transparent border-0 outline-none py-3 sm:py-2.5 text-base sm:text-sm text-gray-800 dark:text-gray-200 [color-scheme:light] dark:[color-scheme:dark]";

function openPicker(input: HTMLInputElement | null) {
  if (!input) return;
  try {
    // Chromium / Safari 16+
    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }
  } catch {
    /* ignore — fall through to focus */
  }
  input.focus();
  input.click();
}

interface DateFieldProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export const DateField: React.FC<DateFieldProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const ref = useRef<HTMLInputElement>(null);

  return (
    <div className={fieldWrap}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => openPicker(ref.current)}
        className="p-1.5 rounded-lg text-[#5932EA] hover:bg-[#5932EA]/10 transition-colors shrink-0"
        aria-label="Open calendar"
      >
        <HiOutlineCalendarDays className="w-5 h-5" />
      </button>
      <input
        ref={ref}
        type="date"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onClick={() => openPicker(ref.current)}
        className={inputClass}
      />
    </div>
  );
};

interface TimeFieldProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export const TimeField: React.FC<TimeFieldProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const ref = useRef<HTMLInputElement>(null);

  return (
    <div className={fieldWrap}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => openPicker(ref.current)}
        className="p-1.5 rounded-lg text-[#5932EA] hover:bg-[#5932EA]/10 transition-colors shrink-0"
        aria-label="Open time picker"
      >
        <HiOutlineClock className="w-5 h-5" />
      </button>
      <input
        ref={ref}
        type="time"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onClick={() => openPicker(ref.current)}
        className={inputClass}
      />
    </div>
  );
};
